import { ROLES } from '@constants';

export const TAB_STATES = {
  ADMIN: 'ADMIN',
  AGENT: 'AGENT'
};

export const TAB_STATES_ACCESS_ROLES = {
  AGENT: [ROLES.OUTBOUND_CALLING_AGENT],
  ADMIN: [ROLES.ADMIN_ROLE, ROLES.OUTBOUND_CALLING_ADMIN]
};

export const OUTBOUND_CALLING_DASHBOARD_STRINGS = {
  HEADING: 'Outbound Calling Dashboard',
  STRING_CONSTANTS: {
    [TAB_STATES.AGENT]: {
      SEARCH_PLACEHOLDER: 'Search by ticket ID, customer name, order ID...',
      NO_TICKETS_FOUND: 'No tickets found',
      NO_TICKETS_MESSAGE: 'Try adjusting your filters or search query',
      AGENT_STATUS_LABEL: 'Agent Status',
      STATS_CALLS_TODAY: 'Calls Today',
      STATS_RESOLVED: 'Resolved',
      STATS_RESOLUTION_RATE: 'Resolution Rate',
      STATS_AVG_DURATION: 'Avg Duration',
      LAST_UPDATED: 'Last updated',
      LOADING_TICKETS: 'Loading tickets...',
      TICKETS_FOUND: 'tickets found'
    },
    [TAB_STATES.ADMIN]: {
      // Main dashboard
      LOADING_ADMIN_STATS: 'Loading admin statistics...',
      NO_STATS_FOUND: 'No statistics available',
      NO_STATS_MESSAGE: 'Unable to load dashboard statistics at this time',
      ERROR_LOADING_STATS: 'Error loading dashboard data',
      CALL_SUPPORT_OVERVIEW: 'Call Support Overview',
      AGENT_AVAILABILITY: 'Agent Availability',
      CALL_ANALYTICS: 'Call Analytics',
      OVERALL_STATS: 'Overall Stats',

      // Agents Dashboard
      AGENTS_DASHBOARD: 'Agents Dashboard',
      LOADING_AGENTS: 'Loading agents...',
      NO_AGENTS_FOUND: 'No agents found',
      NO_AGENTS_MESSAGE: 'No agents available at this time',
      ERROR_LOADING_AGENTS: 'Error loading agents data',
      REFRESH_AGENTS: 'Refresh agents list',
      AGENT_ID: 'Agent ID',
      AGENT_NAME: 'Agent Name',
      AGENT_STATUS: 'Status',
      LAST_PING_AT: 'Last Ping At',

      // Open Tickets Dashboard (Queue)
      QUEUE_DASHBOARD: 'Tickets Queue',
      OPEN_TICKETS_DASHBOARD: 'Open Tickets',
      LOADING_QUEUE: 'Loading tickets queue...',
      NO_QUEUE_TICKETS_FOUND: 'No tickets in queue',
      NO_QUEUE_TICKETS_MESSAGE: 'No open tickets available',
      ERROR_LOADING_QUEUE: 'Error loading tickets queue',

      // Closed Tickets Dashboard
      CLOSED_TICKETS_DASHBOARD: 'Closed Tickets',
      LOADING_CLOSED_TICKETS: 'Loading closed tickets...',
      NO_CLOSED_TICKETS_FOUND: 'No closed tickets found',
      NO_CLOSED_TICKETS_MESSAGE: 'No closed tickets in the selected time range',
      ERROR_LOADING_CLOSED_TICKETS: 'Error loading closed tickets',

      // Table Headers
      TICKET_ID: 'Ticket ID',
      INTERCOM_TICKET_ID: 'Intercom Ticket ID',
      AGENT_ASSIGNED: 'Agent Assigned',
      TICKET_STATUS: 'Status',
      LAST_UPDATED_AT: 'Last Updated',
      CALLBACK_ATTEMPTS: 'Callback Attempts',
      QUEUE_ID: 'Queue ID',
      CREATED_AT: 'Created At',
      ASSIGNED_AT: 'Assigned At',
      ON_CALL_AT: 'On Call At',
      CANCELLED_AT: 'Cancelled At',
      RESOLVED_AT: 'Resolved At',
      FAILED_AT: 'Failed At',

      // Filters
      FILTER_BY_STATUS: 'Filter by Status',
      FILTER_BY_AGENT: 'Filter by Agent',
      FILTER_BY_TICKET_ID: 'Filter by Ticket ID',
      FILTER_LABEL: 'Filters',
      CLEAR_FILTERS: 'Clear Filters',

      // Sort
      SORT_BY: 'Sort By',
      SORT_DIRECTION: 'Sort Direction',
      SORT_ASC: 'Ascending',
      SORT_DESC: 'Descending',

      // Time Range
      TIME_RANGE_LABEL: 'Show tickets from last',
      LAST_1_HOUR: 'Last 1 Hour',
      LAST_6_HOURS: 'Last 6 Hours',
      LAST_12_HOURS: 'Last 12 Hours',
      LAST_24_HOURS: 'Last 24 Hours',
      LAST_48_HOURS: 'Last 48 Hours',

      // Misc
      NA: 'NA',
      ACTIVE: 'Active',
      INACTIVE: 'Inactive',
      SEARCH_BY_TICKET_ID: 'Search by Ticket ID',
      TICKET_ID_LABEL: 'Ticket ID',
      ALL_AGENTS: 'All Agents'
    }
  }
};

// Ticket Status Constants
export const CALLBACK_REQUEST_STATUS = [
  {
    name: 'ASSIGNED',
    value: 'ASSIGNED',
    label: 'Assigned',
    isFilterable: true,
    isTicketActionAllowed: false
  },
  {
    name: 'RESOLVED',
    value: 'RESOLVED',
    label: 'Resolved',
    isFilterable: true,
    isTicketActionAllowed: true
  },
  {
    name: 'ON_CALL',
    value: 'ON_CALL',
    label: 'On Call',
    isFilterable: true,
    isTicketActionAllowed: true
  },
  {
    name: 'FAILED',
    value: 'FAILED',
    label: 'Failed',
    isFilterable: true,
    isTicketActionAllowed: true
  },
  {
    name: 'CANCELLED',
    value: 'CANCELLED',
    label: 'Cancelled',
    isFilterable: true,
    isTicketActionAllowed: false
  }
];

// Backward compatibility helper
export const CALLBACK_REQUEST_STATUS_VALUES = CALLBACK_REQUEST_STATUS.reduce(
  (acc, status) => {
    acc[status.name] = status.value;
    return acc;
  },
  {}
);

// Ticket Priority Constants
export const TICKET_PRIORITY = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL'
};

// Call Outcome Constants
export const CALL_OUTCOME_STATUS = {
  NOT_ATTEMPTED: 'NOT_ATTEMPTED',
  IN_PROGRESS: 'IN_PROGRESS',
  MISSED: 'MISSED',
  REJECTED: 'REJECTED',
  FINISHED: 'FINISHED'
};

// Agent Status Constants
export const AGENT_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  ON_BREAK: 'ON_BREAK',
  IN_CALL: 'IN_CALL'
};

// View Mode Constants
export const VIEW_MODE = {
  AGENT: 'AGENT',
  ADMIN: 'ADMIN'
};

// SLA Status Constants
export const SLA_STATUS = {
  WITHIN_SLA: 'WITHIN_SLA',
  APPROACHING_SLA: 'APPROACHING_SLA',
  BREACHED_SLA: 'BREACHED_SLA'
};

// Default date range offset (last 7 days)
export const OUTBOUND_CALLING_DEFAULT_OFFSET_DAY = -7;

// Ticket polling interval (5 minutes in milliseconds)
export const TICKET_POLLING_INTERVAL_MS = 5 * 60 * 1000;

// Maximum note length for ticket notes and call logs
export const MAX_NOTE_LENGTH = 500;

// NOTE :: ADD Default filters here in future
export const OUTBOUND_CALLING_AGENT_VIEW_DEFAULT_FILTERS = {
  status: [
    CALLBACK_REQUEST_STATUS_VALUES.ASSIGNED
  ]
};

// Status Card IDs of Admin Dashboard
export const STATUS_CARD_IDS = {
  AVAILABLE_AGENTS: 'available-agents',
  QUEUE_LENGTH: 'queue-length',
  COMPLETED_CALLS: 'completed-calls'
};

// Route paths
export const OUTBOUND_CALLING_ROUTES = {
  ADMIN_DASHBOARD: '/outbound-calling/admin',
  AGENTS_LIST: '/outbound-calling/admin/agents',
  TICKETS_QUEUE: '/outbound-calling/admin/tickets/open',
  CLOSED_TICKETS: '/outbound-calling/admin/tickets/closed'
};

// Time range options for closed tickets (in hours)
export const CLOSED_TICKETS_TIME_RANGE = {
  ONE_HOUR: 1,
  SIX_HOURS: 6,
  TWELVE_HOURS: 12,
  TWENTY_FOUR_HOURS: 24,
  FORTY_EIGHT_HOURS: 48
};

// Time range display options
export const TIME_RANGE_OPTIONS = [
  { value: CLOSED_TICKETS_TIME_RANGE.ONE_HOUR, label: '1 Hour' },
  { value: CLOSED_TICKETS_TIME_RANGE.SIX_HOURS, label: '6 Hours' },
  { value: CLOSED_TICKETS_TIME_RANGE.TWELVE_HOURS, label: '12 Hours' },
  { value: CLOSED_TICKETS_TIME_RANGE.TWENTY_FOUR_HOURS, label: '24 Hours' },
  { value: CLOSED_TICKETS_TIME_RANGE.FORTY_EIGHT_HOURS, label: '48 Hours' }
];

// Default page size for admin dashboard tables
export const ADMIN_DASHBOARD_PAGE_SIZE = 10;

export const ADMIN_DASHBOARD_DEBOUNCE_DELAY_MS = 500;

// Sort field constants
export const ADMIN_DASHBOARD_SORT_FIELDS = {
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  ASSIGNED_AT: 'assignedAt',
  ON_CALL_AT: 'onCallAt',
  RESOLVED_AT: 'resolvedAt',
  CANCELLED_AT: 'cancelledAt',
  FAILED_AT: 'failedAt',
  STATUS: 'status'
};

// SORT_DIRECTION lives in '@constants' (shared enum). Import it from there
// in dashboard files rather than re-exporting here.

// Open ticket statuses
export const OPEN_TICKET_STATUSES = ['REQUESTED', 'ASSIGNED', 'ON_CALL'];

// Closed ticket statuses
export const CLOSED_TICKET_STATUSES = ['RESOLVED', 'CANCELLED', 'FAILED', 'EXPIRED', 'REJECTED'];
